<script setup>
import { computed } from 'vue'
import { formatPrice } from '@/Utils/siteAccess'
import { fmtDateTime } from '@/Utils/siteSchedule'

// Live approximation of the card the site renders for a content item, fed by
// the Add / Edit content form. Badges follow the runtime's rules: LIVE for a
// live source, Upcoming while a scheduled start is in the future, a lock for
// registered / paid access, Hidden while unpublished. On Broadcast there is no
// "Free" wording: free items carry no price and a login-required one says
// "Sign in to watch"; paid items are unchanged.
const props = defineProps({
	title: {
		type: String,
		default: '',
	},
	description: {
		type: String,
		default: '',
	},
	poster: {
		type: String,
		default: '',
	},
	/** live | vod | playlist | external */
	kind: {
		type: String,
		default: 'vod',
	},
	/** The item's access level (public / registered / paid). */
	access: {
		type: String,
		default: 'public',
	},
	published: {
		type: Boolean,
		default: true,
	},
	/** ISO of the scheduled start, if any. */
	scheduledStart: {
		type: String,
		default: '',
	},
	categories: {
		type: Array,
		default: () => [],
	},
	/** Plans (products) that unlock this item when `access` is paid. */
	plans: {
		type: Array,
		default: () => [],
	},
	/** Word the site puts on paid items — Theme › Player badge text ("Premium" by default, "Paid" when the badge is off). */
	paidLabel: {
		type: String,
		default: 'Paid',
	},
	compact: {
		type: Boolean,
		default: false,
	},
	broadcast: {
		type: Boolean,
		default: false,
	},
})

const upcoming = computed(() => props.scheduledStart && new Date(props.scheduledStart).getTime() > Date.now())
const typeBadge = computed(() => {
	if (upcoming.value) return { text: `Upcoming · ${fmtDateTime(props.scheduledStart)}`, cls: 'bg-gold text-black' }
	if (props.kind === 'live') return { text: 'LIVE', cls: 'bg-error text-white' }
	if (props.kind === 'playlist') return { text: 'Playlist', cls: 'bg-black/60 text-white' }
	return null
})
const priceLabel = computed(() => {
	if (props.broadcast && props.access !== 'paid') return null
	if (props.access === 'registered') return { text: 'Free with account', cls: 'text-surface-8' }
	if (props.access !== 'paid') return { text: 'Free', cls: 'text-surface-8' }
	const priced = (props.plans || []).filter((p) => p && !p.free && Number(p.price) > 0).sort((a, b) => Number(a.price) - Number(b.price))
	if (!priced.length) {
		if ((props.plans || []).length) return { text: 'Free with a plan', cls: 'text-surface-8' }
		return { text: 'Paid — no plan yet', cls: 'text-gold' }
	}
	const distinct = new Set(priced.map((p) => `${p.price}${p.currency}${p.recurring ? p.period : ''}`))
	return { text: `${distinct.size > 1 ? 'from ' : ''}${formatPrice(priced[0])}`, cls: 'text-white' }
})
const initial = computed(() => (props.title.trim() || 'U').slice(0, 1).toUpperCase())
</script>

<template>
	<div class="overflow-hidden rounded-lg border border-surface-4 bg-black/40 text-left shadow-lg" :class="published ? '' : 'opacity-70'" data-testid="content-card-preview">
		<div class="relative aspect-video w-full bg-surface-3">
			<img v-if="poster" :src="poster" alt="" class="size-full object-cover" data-testid="content-card-poster" @error="$event.target.style.display = 'none'" />
			<div v-else class="grid size-full place-items-center bg-gradient-to-br from-surface-4 to-surface-2 text-3xl font-semibold text-surface-7" data-testid="content-card-placeholder">{{ initial }}</div>
			<div class="absolute left-2 top-2 flex flex-wrap items-center gap-1">
				<span v-if="typeBadge" class="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" :class="typeBadge.cls" data-testid="content-card-type">{{ typeBadge.text }}</span>
				<span v-if="!published" class="rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-surface-9" data-testid="content-card-hidden">Hidden</span>
			</div>
			<span v-if="access !== 'public'" class="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" :title="access === 'paid' ? `Paid access — badge text from Theme › Player (“${paidLabel}”)` : 'Account required'" data-testid="content-card-lock">
				<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
				{{ access === 'paid' ? paidLabel : broadcast ? 'Sign in to watch' : 'Sign in' }}
			</span>
		</div>
		<div class="p-3" :class="compact ? 'py-2' : ''">
			<div class="flex items-start justify-between gap-2">
				<p class="mb-0 min-w-0 truncate font-semibold text-white" :class="title.trim() ? '' : 'text-surface-6'" data-testid="content-card-title">{{ title.trim() || 'Untitled' }}</p>
				<span v-if="priceLabel" class="shrink-0 text-sm font-medium" :class="priceLabel.cls" data-testid="content-card-price">{{ priceLabel.text }}</span>
			</div>
			<p v-if="categories.length" class="mb-0 mt-0.5 truncate text-tiny text-surface-7" data-testid="content-card-categories">{{ categories.join(' · ') }}</p>
			<p v-if="!compact && description.trim()" class="mb-0 mt-1 line-clamp-2 text-tiny text-surface-8">{{ description.trim() }}</p>
		</div>
	</div>
</template>
